import {FormattedMessage, defineMessages, MessageDescriptor} from 'react-intl';
import {
  chat as chatIcon,
  call as callIcon,
  email as emailIcon,
} from '@sail/icons/react/Icon';

import {EligibleChannelsEndpointResponseChannel} from '../../../generated/types';
import {ANY_SUPPORTED_LOCALE} from '../../helpers/localeUtils';
import {Topic} from '../TopicSelector';
import {ChannelType} from './Channel';

type ChannelMessagesKeys =
  | 'chatWithSupport'
  | 'requestACall'
  | 'sendAnEmail'
  | 'alternativeLocaleTitle'
  | 'chatWaitTimePremiumSupport'
  | 'liveWaitTimeEstimate'
  | 'liveOverflowing'
  | 'liveUnavailable'
  | 'emailWaitTimeStandard'
  | 'emailWaitTimeOverflowing'
  | 'emailWaitTimePremiumSupport'
  | 'topicUnavailable';

export const messages: Record<ChannelMessagesKeys, MessageDescriptor> =
  defineMessages({
    chatWithSupport: {
      id: 'support_conversations_client.copilot.link.chatWithSupport',
      description: 'A link that will open a support chat window',
      defaultMessage: 'Chat with us',
    },
    requestACall: {
      id: 'support_conversations_client.copilot.link.requestACall',
      description: 'A link that will open a request a call form window',
      defaultMessage: 'Have us call you',
    },
    sendAnEmail: {
      id: 'support_conversations_client.copilot.link.sendAnEmail',
      description:
        'A link that will open a way for a user to send a support email',
      defaultMessage: 'Send us an email',
    },
    alternativeLocaleTitle: {
      id: 'support_conversations_client.channelSelector.alternativeLocaleTitle',
      description:
        'Title for channel when channel is offered in an alternative locale',
      defaultMessage: '{title} ({language})',
    },
    chatWaitTimePremiumSupport: {
      id: 'support_conversations_client.channelSelector.chat.waitTime',
      description:
        'Hard-coded wait time for chat option in the channel selector',
      defaultMessage: '~3 minute wait',
    },
    liveWaitTimeEstimate: {
      id: 'support_conversations_client.channelSelector.live.waitTimeEstimate',
      description: 'Wait time estimate in minutes for phone or chat',
      defaultMessage: '~{minutes} minute wait',
    },
    liveOverflowing: {
      id: 'support_conversations_client.channelSelector.live.overflowing',
      description:
        'Message if the wait time for phone or chat is longer than usual',
      defaultMessage: 'Longer than usual',
    },
    liveUnavailable: {
      id: 'support_conversations_client.channelSelector.live.unavailable',
      description: 'Message if a live channel is unavailable',
      defaultMessage: 'Currently unavailable',
    },
    emailWaitTimeStandard: {
      id: 'support_conversations_client.channelSelector.email.waitTimeEstimateStandard',
      description: 'Wait time estimate for email option for standard support',
      defaultMessage: '1 business day',
    },
    emailWaitTimeOverflowing: {
      id: 'support_conversations_client.channelSelector.email.waitTimeEstimateOverflowing',
      description:
        'Wait time estimate for email option for standard support when longer than usual',
      defaultMessage: '1-2 business days',
    },
    emailWaitTimePremiumSupport: {
      id: 'support_conversations_client.channelSelector.email.waitTimeEstimatePremiumSupport',
      description:
        'Wait time estimate for email option for Premium Support users',
      defaultMessage: '~4 hour wait',
    },
    topicUnavailable: {
      id: 'support_conversations_client.channelSelector.live.topicUnavailable',
      description:
        'Message if the selected topic is unavailable in the support channel',
      defaultMessage: 'Unavailable for selected topic',
    },
  });

export const getWaitTime = (
  channel: EligibleChannelsEndpointResponseChannel,
  isPremiumSupport: boolean,
  ineligibleTopic: boolean,
) => {
  if (!channel.operational) {
    return <FormattedMessage {...messages.liveUnavailable} />;
  } else if (ineligibleTopic) {
    return <FormattedMessage {...messages.topicUnavailable} />;
  } else if (channel.overflowing) {
    if (channel.type === 'email') {
      return isPremiumSupport ? (
        <FormattedMessage {...messages.emailWaitTimePremiumSupport} />
      ) : (
        <FormattedMessage {...messages.emailWaitTimeOverflowing} />
      );
    } else {
      return <FormattedMessage {...messages.liveOverflowing} />;
    }
  }

  switch (channel.type) {
    case 'request_a_call':
      return (
        <FormattedMessage
          {...messages.liveWaitTimeEstimate}
          values={{
            minutes: channel.waitTime,
          }}
        />
      );
    case 'chat':
      return isPremiumSupport ? (
        <FormattedMessage {...messages.chatWaitTimePremiumSupport} />
      ) : (
        <FormattedMessage
          {...messages.liveWaitTimeEstimate}
          values={{
            minutes: channel.waitTime,
          }}
        />
      );
    case 'email':
      return isPremiumSupport ? (
        <FormattedMessage {...messages.emailWaitTimePremiumSupport} />
      ) : (
        <FormattedMessage {...messages.emailWaitTimeStandard} />
      );
    default:
      return null;
  }
};

const getTitleForChannel = (
  channel: EligibleChannelsEndpointResponseChannel,
  currentLocale: string,
) => {
  let title = <FormattedMessage {...messages.sendAnEmail} />;
  if (channel.type === 'chat') {
    title = <FormattedMessage {...messages.chatWithSupport} />;
  } else if (channel.type === 'request_a_call') {
    title = <FormattedMessage {...messages.requestACall} />;
  }

  if (
    'language' in channel &&
    'locale' in channel &&
    channel.locale !== currentLocale
  ) {
    return (
      <FormattedMessage
        {...messages.alternativeLocaleTitle}
        values={{
          title,
          language: channel.language,
        }}
      />
    );
  }

  return title;
};

export const getDisplayChannels = (props: {
  channels: Array<EligibleChannelsEndpointResponseChannel> | null | undefined;
  isPremiumSupport: boolean;
  selectedTopic: Topic | null | undefined;
  disableTopicSelector?: boolean;
  locale: string;
  showUnavailableChannels: boolean;
}): Array<ChannelType> => {
  const channels = props.channels || [];
  const displayChannels: Array<ChannelType> = [];

  channels.forEach((channel) => {
    const ineligibleTopic = !!(
      props.selectedTopic &&
      channel.ineligibleTopics.includes(props.selectedTopic)
    );

    const disabled =
      !channel.operational ||
      (!props.selectedTopic && !props.disableTopicSelector) ||
      ineligibleTopic;

    if (!props.showUnavailableChannels && disabled) {
      return;
    }

    if (channel.type === 'chat') {
      displayChannels.push({
        title: getTitleForChannel(channel, props.locale),
        disabled,
        waitEstimate: getWaitTime(
          channel,
          props.isPremiumSupport,
          ineligibleTopic,
        ),
        icon: chatIcon,
        locale: channel.locale,
        type: channel.type,
      });
    } else if (channel.type === 'request_a_call') {
      displayChannels.push({
        title: getTitleForChannel(channel, props.locale),
        disabled,
        waitEstimate: getWaitTime(
          channel,
          props.isPremiumSupport,
          ineligibleTopic,
        ),
        icon: callIcon,
        locale: channel.locale,
        type: channel.type,
      });
    } else if (channel.type === 'email') {
      displayChannels.push({
        title: getTitleForChannel(channel, props.locale),
        waitEstimate: getWaitTime(
          channel,
          props.isPremiumSupport,
          ineligibleTopic,
        ),
        disabled,
        icon: emailIcon,
        type: 'email',
        locale: ANY_SUPPORTED_LOCALE,
      });
    }
  });

  return displayChannels;
};
